import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type { ViewportEngine } from '../../viewer/ViewportEngine';
import { SettingsForm } from './SettingsForm';
import { currentViewRegion, needsRegion, takesRegion } from './region';
import { editableValues, initialValues } from './settings/resolve';
import { validateSettings } from './settings/validate';
import { permittedUseLabel } from './states';
import type { Action, StartRunRequest } from './types';

export interface RunDialogProps {
  action: Action;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The slide on screen, for "Current view". */
  engine: ViewportEngine | null;
  onSubmit: (request: StartRunRequest) => void;
  pending?: boolean;
  /** The server's sentence when it refused the run. */
  error?: string | null;
}

type Where = 'image' | 'view';

/**
 * Run an action on the open image: its settings, prefilled with what a run in
 * the image's workspace starts from (the server cascades the action's
 * defaults, the deployment's and the workspace's values), those a run may not
 * change shown locked, and, for an action that takes a region, the whole image
 * or just the current view.
 */
export function RunDialog({ action, open, onOpenChange, engine, onSubmit, pending = false, error }: RunDialogProps) {
  const start = useMemo(() => initialValues(action.settings), [action]);
  const [values, setValues] = useState(start);
  const required = needsRegion(action);
  const [where, setWhere] = useState<Where>(required ? 'view' : 'image');
  // Every opening starts from the workspace's values again — the opening, not
  // a refetch of the actions while the dialog is up.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setValues(start);
      setWhere(required ? 'view' : 'image');
    }
    wasOpen.current = open;
  }, [open, start, required]);

  // Nothing changed for this run: every value is what it started from.
  const atStart = Object.keys(start).every((k) => Object.is(values[k], start[k]));
  const errors = validateSettings(action.settings, values, 'run');
  const invalid = Object.keys(errors).length > 0;
  const regional = takesRegion(action);
  const canView = !!engine && currentViewRegion(engine) !== null;
  const use = permittedUseLabel(action.permittedUses);

  const blocked = required && !canView;
  const submit = () => {
    if (invalid || pending || blocked) return;
    const region = regional && where === 'view' ? currentViewRegion(engine) : null;
    onSubmit({
      action: action.id,
      settings: editableValues(action.settings, values, 'run'),
      ...(region ? { region } : {}),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Run {action.name}</DialogTitle>
          <DialogDescription>
            {action.summary ? `${action.summary} ` : ''}
            {use ? <span className="whitespace-nowrap">{use}.</span> : null}
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-5"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          {regional ? (
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-foreground">Where</span>
              <div role="radiogroup" aria-label="Where to run" className="inline-flex self-start rounded-md border p-0.5">
                {(
                  [
                    ['image', 'Whole image'],
                    ['view', 'Current view'],
                  ] as const
                ).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={where === id}
                    disabled={id === 'view' ? !canView : required}
                    onClick={() => setWhere(id)}
                    className={cn(
                      'rounded px-3 py-1 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                      where === id ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {required ? <p className="text-xs text-muted-foreground">This action runs on the part of the image on screen.</p> : null}
            </div>
          ) : null}
          <SettingsForm settings={action.settings} mode="run" values={values} onChange={setValues} disabled={pending} />
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" onClick={() => setValues(start)} disabled={pending || atStart}>
              Reset to defaults
            </Button>
            <span className="flex-1" />
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={invalid || pending || blocked}>
              {pending ? <Loader2 className="animate-spin" /> : null}
              Run
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
